import { LocationId } from '../location';
import { HasId, HasTimestamps, ValidationResult, Validator, validate, clock, getMoment } from '../common';
const moment = getMoment();

export interface RecurringPayment extends HasId, HasTimestamps {
    tokenCustomerId: string;
    amountInCents: number;
    currency: string;
    invoiceDescription: string;
    email: string;
    title: string;
    firstName: string;
    lastName: string;
    country: string;
    cardName: string;
    cardNumber: string;
    cardExpiry: Date;
    accessCode: string;
    startDate: Date;
    endDate: Date | null;
    nextDate: Date;
    nextAmountInCents: number;
    locationId: LocationId;
    invoiceNumber: string | null;
    attemptedNextDate?: Date | null; // Is not really optional, but I don't want to set it to null everywhere
}

export interface RecurringPaymentListDto extends HasId {
    amountInCents: number;
    currency: string;
    startDate: Date;
    endDate: Date | null;
    nextDate: Date;
    nextAmountInCents: number;
}

export type RecurringPaymentDetailDto = RecurringPaymentListDto;

export function isRecurringPaymentEditable(recurringPayment: RecurringPaymentDetailDto): boolean {
    return (
        moment().isSameOrBefore(recurringPayment.startDate) ||
        !recurringPayment.endDate ||
        moment().isSameOrBefore(recurringPayment.endDate)
    );
}

export function isRecurringPaymentDeletable(recurringPayment: RecurringPaymentDetailDto): boolean {
    return moment().isSameOrBefore(recurringPayment.startDate);
}

export function isRecurringPaymentNextDateInactive(recurringPayment: RecurringPaymentDetailDto): boolean {
    return !!recurringPayment.endDate && moment(recurringPayment.nextDate).isSameOrAfter(recurringPayment.endDate);
}

const startDateIsAfterTodayValidation = (rp: RecurringPaymentDetailDto) =>
    moment(rp.startDate).isAfter(clock.now()) || 'Start date must be after today';
const nextDateIsOnOrAfterStartDateValidation = (rp: RecurringPaymentDetailDto) =>
    moment(rp.nextDate).isSameOrAfter(rp.startDate) || 'Next date must be on or after the start date';
const nextDateIsBeforeEndDateValidation = (rp: RecurringPaymentDetailDto) =>
    rp.endDate === null || moment(rp.nextDate).isBefore(rp.endDate) || 'Next date must be before the end date';
const endDateIsAfterStartDateValidation = (rp: RecurringPaymentDetailDto) =>
    !rp.endDate || moment(rp.endDate).isAfter(rp.startDate) || 'End date must be after the start date';
const endDateIsAfterTodayValidation = (rp: RecurringPaymentDetailDto) =>
    !rp.endDate || moment(rp.endDate).isAfter(clock.now()) || 'End date must be after today';

const NewRecurringPaymentValidations: Array<Validator<RecurringPaymentDetailDto>> = [
    startDateIsAfterTodayValidation,
    nextDateIsOnOrAfterStartDateValidation,
    nextDateIsBeforeEndDateValidation,
    endDateIsAfterStartDateValidation,
];

const UpdateExistingFutureRecurringPaymentValidations: Array<Validator<RecurringPaymentDetailDto>> = [
    startDateIsAfterTodayValidation,
    nextDateIsOnOrAfterStartDateValidation,
    endDateIsAfterStartDateValidation,
    endDateIsAfterTodayValidation,
];

const UpdateExistingActiveRecurringPaymentValidations: Array<Validator<RecurringPaymentDetailDto>> = [
    nextDateIsOnOrAfterStartDateValidation,
    endDateIsAfterStartDateValidation,
    endDateIsAfterTodayValidation,
];

export function validateNewRecurringPayment(recurringPaymentData: RecurringPaymentDetailDto): ValidationResult {
    return validate(NewRecurringPaymentValidations, recurringPaymentData);
}

export function validateUpdateExistingFutureRecurringPayment(
    recurringPaymentData: RecurringPaymentDetailDto,
): ValidationResult {
    return validate(UpdateExistingFutureRecurringPaymentValidations, recurringPaymentData);
}

export function validateUpdateExistingActiveRecurringPayment(
    recurringPaymentData: RecurringPaymentDetailDto,
): ValidationResult {
    return validate(UpdateExistingActiveRecurringPaymentValidations, recurringPaymentData);
}

const INVOICE_DESCRIPTION_PREFIX = 'Digital Stack subscription - ';
const INVOICE_DESCRIPTION_MAX_LENGTH = 64;
const INVOICE_DESCRIPTION_SUFFIX_MAX_LENGTH = INVOICE_DESCRIPTION_MAX_LENGTH - INVOICE_DESCRIPTION_PREFIX.length;
export function generateInvoiceDescription(locationTitle: string): string {
    let suffix = locationTitle;
    if (suffix.length > INVOICE_DESCRIPTION_SUFFIX_MAX_LENGTH) {
        suffix = `${locationTitle.substr(0, INVOICE_DESCRIPTION_SUFFIX_MAX_LENGTH - 3)}...`;
    }
    return INVOICE_DESCRIPTION_PREFIX + suffix;
}

export function getDaysSincePrevious(
    rp: RecurringPaymentListDto,
    recurringPayments: RecurringPaymentListDto[],
): number {
    const index = recurringPayments.indexOf(rp);
    if (index === recurringPayments.length - 1) {
        return 0;
    } else {
        const previous = recurringPayments[index + 1];
        if (previous.endDate === null) {
            return 0;
        }
        const gap = moment(rp.startDate).diff(previous.endDate, 'days');
        return Math.abs(gap);
    }
}

export function getDaysUntilNext(rp: RecurringPaymentListDto, recurringPayments: RecurringPaymentListDto[]): number {
    const index = recurringPayments.indexOf(rp);
    if (index === 0 || rp.endDate === null) {
        return 0;
    } else {
        const next = recurringPayments[index - 1];
        const gap = moment(rp.endDate).diff(next.startDate, 'days');
        return Math.abs(gap);
    }
}
